import { useState, useRef, useEffect } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Icon from "@amzn/meridian/esm/components/icon";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import star from "../../../../images/star.svg";
import { THEME_COLOR } from "../../constants";
import AssistantOptions from "./AssistantOptions";
import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";
import LogManager from "../../../utils/LogManager";

const Assistant = ({
  rtmsRequesterName,
  showBanner,
  setShowBanner,
  meetingUuid,
  meetingId,
  rtmsStreamId,
  rtmsStartTime,
  alias,
  chatMessages,
  setChatMessages,
  isChatLoading,
  setIsChatLoading,
}) => {
  const [showOptions, setShowOptions] = useState(true);
  const sessionIdRef = useRef(`${alias || "user"}-${Date.now()}-${crypto.randomUUID()}`);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const dismissBanner = () => {
    setShowBanner(false);
  };

  const handleSend = async (message) => {
    if (isChatLoading) return;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setShowOptions(false);
    setShowBanner(false);
    setChatMessages((prev) => [...prev, { role: "user", text: message }]);
    setIsChatLoading(true);

    try {
      const requestBody = {
        prompt: message,
        sessionId: sessionIdRef.current,
        meetingMeta: {
          meetingId: meetingId || "",
          meetingUuid: meetingUuid || "",
          rtmsStreamId: rtmsStreamId || "",
          rtmsStartTime,
        },
      };
      if (alias) requestBody.alias = alias;

      const bodyString = JSON.stringify(requestBody);
      const bodyBytes = new TextEncoder().encode(bodyString);
      const hashBuffer = await crypto.subtle.digest("SHA-256", bodyBytes);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const bodyHash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

      let response = await fetch("/invokeAgent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-amz-content-sha256": bodyHash,
        },
        body: bodyString,
        redirect: "manual",
        signal: controller.signal,
      });

      if (response.status === 307 || response.type === "opaqueredirect") {
        response = await fetch("/invokeAgent", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-amz-content-sha256": bodyHash,
          },
          body: bodyString,
          signal: controller.signal,
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setChatMessages((prev) => [...prev, { role: "agent", text: "" }]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data:")) continue;
          const payload = trimmed.substring(5).trim();
          if (!payload.startsWith("{")) continue;
          try {
            const json = JSON.parse(payload);
            const deltaText = json?.event?.contentBlockDelta?.delta?.text;
            if (deltaText) {
              fullText += deltaText;
              setChatMessages((prev) => {
                if (prev.length === 0) return prev;
                const updated = [...prev];
                updated[updated.length - 1] = { role: "agent", text: fullText };
                return updated;
              });
            }
            const msgContent = json?.message?.content;
            if (json?.message?.role === "assistant" && Array.isArray(msgContent)) {
              for (const block of msgContent) {
                if (block.text && !fullText) {
                  fullText = block.text;
                  setChatMessages((prev) => {
                    if (prev.length === 0) return prev;
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: "agent", text: fullText };
                    return updated;
                  });
                }
              }
            }
          } catch (parseErr) {
            continue;
          }
        }
      }

      if (!fullText) {
        setChatMessages((prev) => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          updated[updated.length - 1] = { role: "agent", text: "(No response)" };
          return updated;
        });
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      LogManager.error("Agent call failed:", err);
      LogManager.metric("Assistant_ServerError_Shown", 1, alias ? { alias } : {});
      const errorText =
        "😵 Our server is taking an unexpected break!\nMind trying again in a few minutes? I'll be right here to help.";
      setChatMessages((prev) => {
        if (prev.length === 0) return prev;
        const updated = [...prev];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg && lastMsg.role === "agent" && !lastMsg.text) {
          updated[updated.length - 1] = { role: "agent", text: errorText };
        } else {
          updated.push({ role: "agent", text: errorText });
        }
        return updated;
      });
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleNewSession = () => {
    abortControllerRef.current?.abort();
    setShowOptions(true);
    setChatMessages([]);
    setIsChatLoading(false);
  };

  const hasMessages = chatMessages.length > 0;

  return (
    <Column spacing="none" height="calc(100vh - 131px)" heights={["fill", "fit"]}>
      {/* Top section: banner + chat messages */}
      <Column spacing="none" overflowY="auto">
        {showBanner && (
          <>
            <Row height="20px"/>
            <Row alignmentHorizontal="center" spacing="none">
              <div style={{ position: 'relative' }}>
                <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.LIGHT_TEAL }}>
                  <Box type="outline" width="252px" spacingInset="400">
                    <Row spacing="none" alignmentVertical="top">
                      <img src={star} alt="star" />
                      <Column width="8px" />
                      <Column spacing="none">
                        <Text fontFamily="amazonEmber" type="h50" alignment="start">
                          {rtmsRequesterName}
                        </Text>
                        <Text fontFamily="amazonEmber" type="b200" alignment="start">
                          turned on meeting summary
                        </Text>
                      </Column>
                    </Row>
                  </Box>
                </Theme>
                <div style={{ position: 'absolute', top: '2px', right: '2px' }}>
                  <Button type="icon" size="small" onClick={dismissBanner}>
                    <Icon tokens={closeSmallTokens} color={THEME_COLOR.MID_GRAY}>Close</Icon>
                  </Button>
                </div>
              </div>
            </Row>
          </>
        )}
        {hasMessages && (
          <Row alignmentHorizontal="center" spacing="none">
            <ChatMessages messages={chatMessages} isLoading={isChatLoading} />
          </Row>
        )}
      </Column>

      {/* Bottom section: options + chat input */}
      <Row alignmentHorizontal="center" spacing="none">
        <Column spacing="none">
          {showOptions && !hasMessages && (
            <>
              <AssistantOptions onOptionSelect={handleSend} alias={alias} />
              <Row height="8px" />
            </>
          )}
          <ChatInput onSend={handleSend} onNewSession={handleNewSession} disabled={isChatLoading} />
          <Row height="12px" />
        </Column>
      </Row>
    </Column>
  );
};

export default Assistant;
